import React from 'react';

import type { Block } from '@gitbook/slate';
import type { BlockProps } from '@gitbook/slate-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { filterOutNullable } from '@gitbook/util-ts';

import { useNavigation } from '../../../navigation';
import { BlockWrapper, useBlockLayoutGroupFactory, useBlockPaletteFactory } from '../common';
import { EditorSectionIDsContext } from '../contexts';
import { BlockTabsBody } from './BlockTabsBody';
import { BlockTabsHeader } from './BlockTabsHeader';
import { BlockTabsHeaderItem } from './BlockTabsHeaderItem';
import { BlockTabsItemContainer } from './BlockTabsItemContainer';
import { useTabsState } from './useTabsState';

/**
 * Component to render a tabs block (mapped in the renderer).
 */
export function BlockTabs(props: BlockProps): React.ReactElement {
    const { node, children, editor } = props;
    const { navigateToHash } = useNavigation();

    const { sections: tabSectionIDs } = React.useContext(EditorSectionIDsContext);
    const [activeTab, selectTab] = useTabsState(node);

    // Select a tab based on user selection
    const onSelectTab = useEventCallback((tab: Block) => {
        if (tab.equals(activeTab)) {
            return;
        }
        if (editor.readOnly) {
            selectTab(tab);
        } else {
            // If the Editor is editable, focus the contents of the tab when switching to it.
            editor.change((change) => {
                // Selection has to be blurred before selecting a tab because of:
                // https://github.com/ianstormtaylor/slate/issues/1769
                change.deselect();
                selectTab(tab);

                // Convert the selection to a TextRange and collapse it to the end of the first text node in the tab.
                const text = tab.getLastText();
                if (text) {
                    change.select({
                        anchorKey: text.key,
                        focusKey: text.key,
                        anchorOffset: 0,
                        focusOffset: 0,
                        isBackward: false,
                        isAtomic: true,
                        isFocused: true,
                    });
                    change.collapseToEndOf(text);
                }
            });
        }
    });

    const currentTabsBlockKeys = node.blockNodes.map((tab) => tab.key);
    const currentTabsBlockSectionIds = [...tabSectionIDs.entries()].filter(([key]) =>
        currentTabsBlockKeys.includes(key)
    );

    // Tabs header
    const headerContent = node.blockNodes.map((tab, index) => {
        const tabSectionId = currentTabsBlockSectionIds.find(([key]) => key === tab.key)?.[1] ?? '';
        const isActive = tab.key === activeTab.key;

        return (
            <BlockTabsHeaderItem
                key={tab.key}
                editor={editor}
                tab={tab}
                index={index}
                id={tabSectionId}
                active={isActive}
                isFirst={index === 0}
                isLast={index === node.nodes.size - 1}
                onSelectTab={(selectedTab) => {
                    onSelectTab(selectedTab);
                    navigateToHash(tabSectionId, { scroll: false });
                }}
            />
        );
    });

    // Tabs content
    const bodyContent = React.Children.map(children, (child, index) => {
        const currentTabKey =
            child && typeof child === 'object' && 'key' in child ? child.key : index;
        const isActive = currentTabKey === activeTab.key;

        return (
            <BlockTabsItemContainer
                key={child && typeof child === 'object' && 'key' in child ? child.key : index}
                active={isActive}
            >
                {child}
            </BlockTabsItemContainer>
        );
    });

    // Block options palette
    const nodeKey = node.key;
    const createLayoutGroup = useBlockLayoutGroupFactory({ nodeKey });
    const palette = useBlockPaletteFactory({
        nodeKey,
        createExtraGroups: () => {
            const layoutGroup = createLayoutGroup();
            return [layoutGroup].filter(filterOutNullable);
        },
    });

    return (
        <BlockWrapper palette={palette} {...props}>
            <div className="group/tabs">
                <BlockTabsHeader>{headerContent}</BlockTabsHeader>
                <BlockTabsBody>{bodyContent}</BlockTabsBody>
            </div>
        </BlockWrapper>
    );
}
